import type { MouseEvent } from 'react'
import { MenuItem, Divider, Button, Box } from '@chakra-ui/react'
import { Edit, Trash2, RotateCcw, Download, ArrowRight } from 'lucide-react'
import type { DataTableOptions, Permission, RowActionItem } from './types'

export interface BuildRowActionItemsParams {
  row: any
  rowId: any
  delDate: any
  attachment?: string
  options: DataTableOptions
  effectivePerm: Permission
  onAction?: (id: any, action: string) => void
  /** When true, otherActions use menu labels instead of inline button renders */
  forceMenuItems?: boolean
}

export function buildRowActionItems({
  row,
  rowId,
  delDate,
  attachment,
  options,
  effectivePerm,
  onAction,
  forceMenuItems = false,
}: BuildRowActionItemsParams): RowActionItem[] {
  const { dataSource, softDelete, displayInModal, otherActions } = options
  const items: RowActionItem[] = []

  if (!delDate && effectivePerm?.can_edit) {
    if (displayInModal) {
      items.push({
        key: 'edit',
        label: 'Edit',
        icon: <Edit size={16} />,
        onClick: () => onAction?.(row, 'Edit'),
      })
    } else {
      items.push({
        key: 'edit',
        label: 'Edit',
        icon: <Edit size={16} />,
        to: `${dataSource}/${rowId}`,
      })
    }
  }

  if (attachment && !delDate) {
    items.push({
      key: 'download',
      label: 'Download',
      icon: <Download size={16} />,
      href: attachment,
      target: '_blank',
      download: true,
    })
  }

  const visibleOtherActions =
    Array.isArray(otherActions) && !delDate ? otherActions.filter((action) => !action.show || action.show(row)) : []

  const shouldSkipOtherActionsAsMenu =
    !forceMenuItems &&
    visibleOtherActions.length > 0 &&
    items.length === 0 &&
    !delDate &&
    visibleOtherActions.every((action) => action.render)

  if (!shouldSkipOtherActionsAsMenu && Array.isArray(otherActions) && !delDate) {
    otherActions
      .filter((action) => !action.show || action.show(row))
      .forEach((action, index) => {
        if (action.onClick) {
          items.push({
            key: `other-${index}`,
            label: action.name,
            icon: <ArrowRight size={16} />,
            onClick: () => action.onClick?.(row),
          })
        } else if (action.link) {
          items.push({
            key: `other-${index}`,
            label: action.name,
            icon: <ArrowRight size={16} />,
            to: `${action.link}/${rowId}`,
          })
        }
      })
  }

  const needsDividerBeforeDestructive = items.length > 0 && (effectivePerm?.can_delete || delDate)
  let dividerUsed = false

  const withDivider = (): boolean => {
    if (!dividerUsed && needsDividerBeforeDestructive) {
      dividerUsed = true
      return true
    }
    return false
  }

  if (delDate && effectivePerm?.can_delete) {
    items.push({
      key: 'restore',
      label: 'Restore',
      icon: <RotateCcw size={16} />,
      color: 'green.600',
      dividerBefore: withDivider(),
      onClick: () => onAction?.(rowId, 'Restore'),
    })
  }

  if (softDelete && !delDate && effectivePerm?.can_delete) {
    items.push({
      key: 'trash',
      label: 'Trash',
      icon: <Trash2 size={16} />,
      color: 'orange.600',
      dividerBefore: withDivider(),
      onClick: () => onAction?.(rowId, 'Trash'),
    })
  }

  if (softDelete && delDate && effectivePerm?.can_delete) {
    items.push({
      key: 'delete',
      label: 'Delete Permanently',
      icon: <Trash2 size={16} />,
      color: 'red.600',
      dividerBefore: withDivider(),
      onClick: () => onAction?.(rowId, 'Delete'),
    })
  }

  return items
}

export function shouldRenderOtherActionsAsButtons(
  row: any,
  delDate: any,
  options: DataTableOptions,
  baseItemCount: number,
  forceMenuItems: boolean
): boolean {
  if (forceMenuItems) return false

  const { otherActions } = options
  const visibleOtherActions =
    Array.isArray(otherActions) && !delDate ? otherActions.filter((action) => !action.show || action.show(row)) : []

  return (
    visibleOtherActions.length > 0 &&
    baseItemCount === 0 &&
    !delDate &&
    visibleOtherActions.every((action) => action.render)
  )
}

export function shouldRenderSingleOtherActionButton(
  row: any,
  delDate: any,
  options: DataTableOptions,
  itemCount: number,
  forceMenuItems: boolean
): boolean {
  if (forceMenuItems) return false

  const { otherActions } = options
  return (
    Array.isArray(otherActions) &&
    otherActions.length === 1 &&
    itemCount === 0 &&
    !delDate &&
    !!otherActions[0].onClick &&
    (!otherActions[0].show || otherActions[0].show(row))
  )
}

export function renderRowContextMenuItems(
  items: RowActionItem[],
  RouterLinkComponent: typeof import('react-router-dom').Link,
  onItemClick?: () => void,
  hoverBg = 'gray.100'
) {
  return items.map((item) => {
    const handleClick = (e: MouseEvent) => {
      e.stopPropagation()
      item.onClick?.()
      onItemClick?.()
    }

    const commonProps = {
      variant: 'ghost' as const,
      w: '100%',
      justifyContent: 'flex-start' as const,
      fontWeight: 'normal' as const,
      borderRadius: 0,
      size: 'sm' as const,
      h: 'auto' as const,
      py: 2,
      px: 3,
      color: item.color,
      leftIcon: item.icon ? <Box flexShrink={0}>{item.icon}</Box> : undefined,
      _hover: { bg: hoverBg },
      onClick: handleClick,
      role: 'menuitem' as const,
    }

    return (
      <Box key={item.key}>
        {item.dividerBefore && <Divider my={1} />}
        {item.to ? (
          <Button {...commonProps} as={RouterLinkComponent} to={item.to}>
            {item.label}
          </Button>
        ) : item.href ? (
          <Button
            {...commonProps}
            as="a"
            href={item.href}
            target={item.target}
            download={item.download}
          >
            {item.label}
          </Button>
        ) : (
          <Button {...commonProps}>{item.label}</Button>
        )}
      </Box>
    )
  })
}

export function renderRowActionMenuItems(
  items: RowActionItem[],
  RouterLinkComponent: typeof import('react-router-dom').Link,
  onItemClick?: () => void
) {
  return items.map((item) => {
    const handleClick = (e: MouseEvent) => {
      e.stopPropagation()
      item.onClick?.()
      onItemClick?.()
    }

    const commonProps = {
      icon: item.icon,
      color: item.color,
      onClick: handleClick,
    }

    return (
      <span key={item.key}>
        {item.dividerBefore && <Divider />}
        {item.to ? (
          <MenuItem {...commonProps} as={RouterLinkComponent} to={item.to}>
            {item.label}
          </MenuItem>
        ) : item.href ? (
          <MenuItem
            {...commonProps}
            as="a"
            href={item.href}
            target={item.target}
            download={item.download}
          >
            {item.label}
          </MenuItem>
        ) : (
          <MenuItem {...commonProps}>{item.label}</MenuItem>
        )}
      </span>
    )
  })
}
